import React from "react";
import HeaderWithSearch from "./HeaderWithSearch";

function GameCard({ emoji, emojiColor, title, description, filename, instructions }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 18,
        boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
        padding: 24,
        width: 360,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          height: 200,
          borderRadius: 18,
          marginBottom: 18,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #0f1226 0%, #1a1f3d 100%)",
          color: emojiColor,
          fontSize: "5rem",
        }}
        aria-hidden="true"
      >
        <span role="img" aria-label={title}>
          {emoji}
        </span>
      </div>
      <h2 style={{ fontSize: "1.4rem", fontWeight: 600, marginBottom: 8 }}>
        {title}
      </h2>
      <p
        style={{
          fontSize: "1.05rem",
          color: "#444",
          marginBottom: 16,
          minHeight: 72,
        }}
      >
        {description}
      </p>

      <a
        href={`/${filename}`}
        download={filename}
        style={{
          display: "inline-block",
          background: "#1976d2",
          color: "#fff",
          fontWeight: 600,
          textDecoration: "none",
          fontSize: "1.05rem",
          padding: "10px 18px",
          borderRadius: 10,
          marginBottom: 10,
        }}
      >
        Download {filename}
      </a>

      <a
        href={`/${filename}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#1976d2",
          fontWeight: 600,
          textDecoration: "underline",
          fontSize: "0.98rem",
        }}
      >
        Or play it online
      </a>

      <div
        style={{
          marginTop: 12,
          fontSize: "0.95rem",
          color: "#555",
          textAlign: "left",
          width: "100%",
        }}
      >
        <strong>How to play offline:</strong>
        <ol style={{ paddingLeft: 20, margin: "6px 0 0 0" }}>
          {instructions.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      </div>
    </div>
  );
}

// Patch notes feed for the Offline Games page. Newest entries first.
// Whenever an offline game ships a HUGE update, prepend an entry here.
const PATCH_FEED = [
  {
    game: "Game Pack",
    version: "Arcade glow-up",
    date: "2026-05-12",
    color: "#7ce7ff",
    changes: [
      "⚽ Neon Soccer Duel rebuilt with a stadium-style visual overhaul: crowd lights, goal nets, richer player sprites, better HUD, and heavier impact effects.",
      "🏀 NEW Basket Random — one-button basketball chaos with jump-and-swing physics, randomized player builds each round, 2P couch play, and a CPU mode.",
      "🏃 NEW Skyline Sprint — three-lane endless runner with jumps, slides, drones, laser gates, collectible shards, and a glowing rooftop skyline.",
      "🎯 NEW Neon Night Pinball — single-table pinball with a charge plunger, reactive bumpers, orbit spinners, live flippers, and full cabinet art.",
      "🔨 NEW Midnight Mole Mayhem — carnival whack-a-mole with animated characters, golden bonus moles, bomb fake-outs, combo scoring, and pointer hammer swings.",
      "🦖 NEW Meteor Mesa Dino Dash — polished dinosaur runner with parallax mesas, sunset skies, jump-and-duck obstacle flow, and rising speed.",
      "🟪 NEW Neon Flux Dash — geometry-dash-inspired auto-run level with spikes, jump pads, platforms, saws, gaps, and progress tracking.",
      "Game Hub megafile rebuilt — now 25 offline games in one downloadable HTML file.",
    ],
  },
  {
    game: "Game Pack",
    version: "4 GOATED 2P games",
    date: "2026-05-02",
    color: "#7df9ff",
    changes: [
      "⚽ NEW Neon Soccer Duel — couch 2P neon soccer with dash tackles, boost pads, power shots, score shake, and first-to-5 chaos.",
      "🪄 NEW Wizard Wars — 2P spell duel with shields, bouncing magic bolts, cover walls, HP bars, particles, and first wizard standing wins.",
      "🚀 NEW Jetpack Joust — 2P sky arena with jetpack boosts, lance dashes, platforms, aerial knockouts, and first-to-7 scoring.",
      "🛞 NEW Sumo Bumpers — 2P shrinking-ring bumper battle with boosts, powerups, huge knockbacks, and first-to-5 rounds.",
      "Game Hub megafile rebuilt — now 19 offline games, including 9 couch 2-player modes/games.",
    ],
  },
  {
    game: "Game Pack",
    version: "Basketball + QoL",
    date: "2026-04-30",
    color: "#fb923c",
    changes: [
      "🏀 NEW Basketball game — smooth side-view hoops with arc shots, charged power-ups, steals, and rebounds. 1P vs CPU (Easy / Normal / Hard) or 2P couch. First to 21 (or highest after the 2:00 quarter).",
      "💥 Tank Battle now has 1P vs CPU mode (Easy / Normal / Hard) — CPU rotates, predicts, dodges, and respects line-of-sight to walls.",
      "⛶ Fullscreen button added to every offline game (and the megafile launcher) — tap to take over the whole screen.",
      "💻 Chromebook fix: every game canvas now scales to the viewport height, so tall-canvas games like Tetris, Highway Racer, and Flappy no longer get cut off in the bottom 1/4 on short screens.",
      "Performance: Basketball uses sub-stepped physics so it stays smooth even on slower Chromebook hardware.",
      "Game Hub megafile rebuilt — now 15 games (~471 KB) and the player bar has its own ⛶ Fullscreen button.",
    ],
  },
  {
    game: "Game Pack",
    version: "6 new games",
    date: "2026-04-29",
    color: "#a78bfa",
    changes: [
      "💣 Minesweeper — classic logic puzzle, 3 difficulties (9×9 / 16×16 / 24×16), best-time per difficulty.",
      "📝 Wordle — guess the 5-letter word in 6 tries, ~500-word dictionary, streak tracking.",
      "🃏 Solitaire (Klondike) — click-to-move, double-click to send to foundation, undo, draw 1 or draw 3.",
      "🔴 Connect Four — 1P vs CPU (Easy / Normal / Hard with minimax) or 2P couch.",
      "🧱 Breakout — paddle/ball/bricks with endless levels, mouse + touch + keyboard support.",
      "💥 Tank Battle (2P) — top-down arena combat, bouncing bullets, HP, first to 5.",
      "All 6 games auto-included in the Game Hub megafile (now 14 games, 393 KB).",
    ],
  },
  {
    game: "Game Hub",
    version: "Megafile",
    date: "2026-04-29",
    color: "#a78bfa",
    changes: [
      "🎮 Download ALL offline games as a single HTML file (all-games.html, ~395 KB).",
      "Beautiful launcher screen with cards for every game; click to play, Esc to return.",
      "Each game runs in its own sandboxed iframe — no JS/CSS conflicts between games.",
      "Restart button per game · works fully offline · email it / put it on a USB stick.",
      "Auto-rebuilt whenever an individual game changes — `node scripts/build-megafile.js`.",
    ],
  },
  {
    game: "Game Pack",
    version: "5 new games",
    date: "2026-04-29",
    color: "#a78bfa",
    changes: [
      "🧩 2048 — full puzzle with score, best, undo, swipe + WASD/arrows.",
      "🐤 Flappy Bird — gravity, scrolling pipes, persistent best, mobile-friendly.",
      "🟦 Tetris — all 7 tetrominoes, ghost piece, hard drop, line clears, levels, next preview.",
      "🏓 Pong (1 or 2 players) — couch versus or vs CPU (Easy / Normal / Hard), first to 7.",
      "🚴 Tron Lightcycles (2 players) — last cycle alive wins, 3 speeds, first to 5 rounds.",
    ],
  },
  {
    game: "Highway Racer",
    version: "1.2",
    date: "2026-04-29",
    color: "#38bdf8",
    changes: [
      "🛒 Garage & Shop added! Spend coins on car designs and extra lives.",
      "9 unlockable car skins (Cyan Cruiser → Rainbow Streak, 0–1500 🪙).",
      "Extra Lives (75 🪙, max 3 stored): crash with a life in stock and get a 2-second invincibility window with the lane ahead cleared.",
      "Run coins now deposit into a persistent Wallet on game over.",
      "HUD shows Lives ❤️ and Wallet 🪙 alongside the per-run coin count.",
    ],
  },
  {
    game: "Highway Racer",
    version: "1.1",
    date: "2026-04-29",
    color: "#38bdf8",
    changes: [
      "Throttle (↑ / W) and brake (↓ / S) — speed is now player-controlled.",
      "Hard 180 km/h cap; visual scroll is ~2× faster so 130 km/h feels real.",
      "Fuel drain scales quadratically with speed (10 sec at top speed, 100 sec at minimum).",
      "Top-10 leaderboard with date + tier per run, plus a Lifetime Stats panel.",
      "Brake-light glow on the player car when braking.",
      "Mobile gets dedicated GAS / BRAKE buttons.",
      "In-game patch notes panel with a NEW badge.",
    ],
  },
  {
    game: "Chess",
    version: "1.1",
    date: "2026-04-29",
    color: "#fbbf24",
    changes: [
      "AI now waits at least 1 second before playing each move (smoother pacing).",
      "In-game patch notes panel.",
    ],
  },
  {
    game: "Highway Racer",
    version: "1.0",
    date: "2026-04-29",
    color: "#38bdf8",
    changes: [
      "First release: 4-lane endless racer with score, fuel, speed, coins, and 7 tiers.",
    ],
  },
  {
    game: "Chess",
    version: "1.0",
    date: "2026-04-29",
    color: "#fbbf24",
    changes: [
      "First release: full single-file chess game with built-in AI (4 difficulty levels).",
    ],
  },
  {
    game: "Snake",
    version: "1.0",
    date: "2026-04-29",
    color: "#6ee7b7",
    changes: [
      "First release: classic Snake in one HTML file with 4 speeds and wrap/solid walls.",
    ],
  },
];

function PatchNotesPanel() {
  const [open, setOpen] = React.useState(false);
  const visible = open ? PATCH_FEED : PATCH_FEED.slice(0, 2);
  return (
    <section
      style={{
        maxWidth: 820,
        width: "100%",
        background: "#fff",
        border: "1px solid #e3e7f1",
        borderRadius: 16,
        padding: "18px 22px",
        marginBottom: 32,
        boxShadow: "0 2px 16px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ fontSize: "1.4rem", margin: 0, color: "#1976d2" }}>
          📋 What's New
        </h2>
        <span style={{ fontSize: "0.85rem", color: "#666" }}>
          Patch notes across all offline games
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {visible.map((p, i) => (
          <div
            key={i}
            style={{
              background: "#f7faff",
              border: "1px solid #e3e7f1",
              borderLeft: `4px solid ${p.color}`,
              borderRadius: 10,
              padding: "10px 14px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 6,
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontWeight: 700, color: "#222" }}>
                {p.game}{" "}
                <span style={{ color: p.color, fontWeight: 700 }}>
                  v{p.version}
                </span>
                {i === 0 && (
                  <span
                    style={{
                      background: "#ef4444",
                      color: "#fff",
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      padding: "2px 7px",
                      borderRadius: 8,
                      marginLeft: 8,
                      letterSpacing: "0.5px",
                      verticalAlign: "middle",
                    }}
                  >
                    NEW
                  </span>
                )}
              </span>
              <span style={{ fontSize: "0.8rem", color: "#888" }}>
                {p.date}
              </span>
            </div>
            <ul
              style={{
                margin: "4px 0 0 18px",
                padding: 0,
                color: "#333",
                fontSize: "0.95rem",
                lineHeight: 1.5,
              }}
            >
              {p.changes.map((c, j) => (
                <li key={j} style={{ marginBottom: 2 }}>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {PATCH_FEED.length > 2 && (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          style={{
            marginTop: 14,
            background: "transparent",
            border: "1px solid #1976d2",
            color: "#1976d2",
            borderRadius: 8,
            padding: "8px 16px",
            fontWeight: 600,
            fontSize: "0.9rem",
            cursor: "pointer",
          }}
        >
          {open ? "Show less" : `Show all ${PATCH_FEED.length} updates`}
        </button>
      )}
    </section>
  );
}

function OfflineGames() {
  return (
    <>
      <HeaderWithSearch />
      <main
        style={{
          minHeight: "100vh",
          width: "100%",
          background: "linear-gradient(180deg, #e3f0fc 0%, #fafdff 100%)",
          padding: 0,
          margin: 0,
        }}
      >
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            padding: "60px 20px 40px 20px",
          }}
        >
          <h1
            style={{
              fontSize: "3rem",
              fontWeight: "bold",
              marginBottom: 12,
              textAlign: "center",
            }}
          >
            Offline Downloadable Games for Students
          </h1>
          <p
            style={{
              fontSize: "1.2rem",
              color: "#333",
              marginBottom: 32,
              maxWidth: 720,
              textAlign: "center",
              lineHeight: 1.5,
            }}
          >
            Download these games to your computer and play them anytime without
            an internet connection. Perfect for study breaks, classroom
            activities, or when Wi-Fi isn't around. Just download the file,
            double-click to open it in your browser, and start playing!
          </p>

          <PatchNotesPanel />

          {/* Megafile hero card — all games in one downloadable HTML file */}
          <section
            style={{
              maxWidth: 820,
              width: "100%",
              background:
                "linear-gradient(135deg, #1a1f3d 0%, #2a1f4a 50%, #1d2350 100%)",
              border: "1px solid rgba(110, 231, 183, 0.45)",
              borderRadius: 18,
              padding: "26px 28px",
              marginBottom: 32,
              boxShadow: "0 12px 36px rgba(15, 18, 38, 0.25)",
              color: "#e8ecff",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "inline-block",
                background: "rgba(110, 231, 183, 0.16)",
                color: "#6ee7b7",
                border: "1px solid rgba(110, 231, 183, 0.4)",
                borderRadius: 999,
                padding: "3px 12px",
                fontSize: "0.72rem",
                fontWeight: 800,
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              ✨ New · Single File
            </div>
            <h2
              style={{
                margin: "0 0 8px 0",
                fontSize: "1.7rem",
                background:
                  "linear-gradient(135deg, #6ee7b7 0%, #38bdf8 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
              }}
            >
              🎮 Offline Game Hub — All 25 Games in One File
            </h2>
            <p
              style={{
                margin: "0 0 18px 0",
                color: "#a4adda",
                fontSize: "1rem",
                lineHeight: 1.55,
              }}
            >
              Every offline game on this page, bundled into a single
              HTML file. Save it to a USB stick, email it to a friend, or open
              it directly from your downloads — no internet, no install, no
              accounts. Each game launches in its own sandbox so they can't
              break each other. Each game now has a ⛶ Fullscreen button.
            </p>
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <a
                href="/all-games.html"
                download="all-games.html"
                style={{
                  display: "inline-block",
                  background: "#6ee7b7",
                  color: "#0f1226",
                  fontWeight: 800,
                  textDecoration: "none",
                  fontSize: "1rem",
                  padding: "12px 22px",
                  borderRadius: 10,
                }}
              >
                ⬇ Download all-games.html
              </a>
              <a
                href="/all-games.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#6ee7b7",
                  fontWeight: 700,
                  textDecoration: "underline",
                  fontSize: "0.95rem",
                }}
              >
                Or open the hub online
              </a>
            </div>
            <p
              style={{
                margin: "16px 0 0 0",
                color: "#8a93c0",
                fontSize: "0.82rem",
                lineHeight: 1.5,
              }}
            >
              Includes Snake, Chess vs AI, Highway Racer, 2048, Flappy, Tetris,
              Pong (1P/2P), Tron Lightcycles (2P), Minesweeper, Wordle,
              Solitaire, Connect Four (1P/2P), Breakout, Tank Battle (1P/2P),
              Basketball (1P/2P), Basket Random, Skyline Sprint, Neon Night Pinball,
              Midnight Mole Mayhem, Meteor Mesa Dino Dash, Neon Flux Dash,
              Neon Soccer Duel (2P), Wizard Wars (2P), Jetpack Joust (2P),
              and Sumo Bumpers (2P).
            </p>
          </section>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 40,
              marginBottom: 40,
            }}
          >
            {/* Snake Game */}
            <GameCard
              emoji="🐍"
              emojiColor="#6ee7b7"
              title="Snake Game"
              description="The classic Snake game in a single HTML file. Works completely offline — no installs needed."
              filename="snake.html"
              instructions={[
                'Click "Download snake.html".',
                "Open the downloaded file in any browser.",
                "Use arrow keys (or swipe) to control the snake.",
              ]}
            />

            {/* Chess Game */}
            <GameCard
              emoji="♞"
              emojiColor="#fafdff"
              title="Chess (vs AI)"
              description="Play chess against a built-in AI. One self-contained HTML file with multiple difficulty levels — works completely offline."
              filename="chess.html"
              instructions={[
                'Click "Download chess.html".',
                "Open the downloaded file in any browser.",
                "Tap a piece, then tap where to move it.",
                "Pick a difficulty in the side panel.",
              ]}
            />

            {/* Highway Racer */}
            <GameCard
              emoji="🏎️"
              emojiColor="#38bdf8"
              title="Highway Racer"
              description="Top-down endless racer. Throttle, brake, fuel, 7 tiers (Rookie → Legend), AND a coin shop with 9 car skins and extra lives. Smooth 60 FPS."
              filename="car_game.html"
              instructions={[
                'Click "Download car_game.html".',
                "Open the downloaded file in any browser.",
                "↑ / W to accelerate · ↓ / S to brake.",
                "← / → (or A / D) to switch lanes.",
                "Grab ⛽ fuel cans, collect 🪙 coins, avoid traffic.",
              ]}
            />

            {/* 2048 */}
            <GameCard
              emoji="🧩"
              emojiColor="#edc22e"
              title="2048"
              description="The classic merge puzzle. Combine matching tiles to reach 2048 (and beyond). Persistent best score, undo, swipe + keyboard."
              filename="2048.html"
              instructions={[
                'Click "Download 2048.html".',
                "Open the downloaded file in any browser.",
                "Use arrow keys / WASD (or swipe) to slide tiles.",
                "Merge same-numbered tiles to grow your score.",
              ]}
            />

            {/* Flappy Bird */}
            <GameCard
              emoji="🐤"
              emojiColor="#fbd24c"
              title="Flappy"
              description="Tap, click, or hit Space to flap. Don't crash into pipes. Best score saved locally. Mobile-friendly."
              filename="flappy.html"
              instructions={[
                'Click "Download flappy.html".',
                "Open the downloaded file in any browser.",
                "Space / ↑ / W (or tap the screen) to flap.",
                "Pass through pipes for points; R to restart.",
              ]}
            />

            {/* Tetris */}
            <GameCard
              emoji="🟦"
              emojiColor="#22d3ee"
              title="Tetris"
              description="Full Tetris with all 7 pieces, ghost piece, hard drop, line clears, increasing levels, and a Next preview. Touch controls included."
              filename="tetris.html"
              instructions={[
                'Click "Download tetris.html".',
                "Open the downloaded file in any browser.",
                "← / → move · ↑ rotate · ↓ soft drop · Space hard drop.",
                "Clear lines for points; level speeds up every 10 lines.",
              ]}
            />

            {/* Pong */}
            <GameCard
              emoji="🏓"
              emojiColor="#38bdf8"
              title="Pong (1 or 2 Players)"
              description="The classic. Play vs CPU (Easy / Normal / Hard) or grab a friend for couch 2-player. First to 7 wins."
              filename="pong.html"
              instructions={[
                'Click "Download pong.html".',
                "Open the downloaded file in any browser.",
                "Left paddle: W / S · Right paddle: ↑ / ↓.",
                "Pick a mode at the bottom; Space to pause.",
              ]}
            />

            {/* Tron Lightcycles */}
            <GameCard
              emoji="🚴"
              emojiColor="#f472b6"
              title="Tron Lightcycles (2P)"
              description="Couch versus on a glowing grid. Don't crash into walls or any trail. 3 speeds, first to 5 rounds wins the match."
              filename="tron.html"
              instructions={[
                'Click "Download tron.html".',
                "Open the downloaded file in any browser.",
                "P1 uses WASD · P2 uses arrow keys.",
                "Last cycle alive wins the round.",
              ]}
            />

            {/* Minesweeper */}
            <GameCard
              emoji="💣"
              emojiColor="#60a5fa"
              title="Minesweeper"
              description="Classic logic puzzle. Reveal cells, flag mines. 3 difficulties (Easy 9×9 to Hard 24×16) with best-time tracking per difficulty."
              filename="minesweeper.html"
              instructions={[
                'Click "Download minesweeper.html".',
                "Open the downloaded file in any browser.",
                "Left click reveal · Right click (or long-press) flag.",
                "Click a number with the right flag count to chord-reveal.",
              ]}
            />

            {/* Wordle */}
            <GameCard
              emoji="📝"
              emojiColor="#34d399"
              title="Wordle"
              description="Guess the 5-letter word in 6 tries. Green/yellow/gray feedback after each guess. Persistent streak counter, ~500-word dictionary, no internet needed."
              filename="wordle.html"
              instructions={[
                'Click "Download wordle.html".',
                "Open the downloaded file in any browser.",
                "Type your guess, press Enter to submit.",
                "Click 'New Word' or press Enter after a win for a fresh round.",
              ]}
            />

            {/* Solitaire */}
            <GameCard
              emoji="🃏"
              emojiColor="#ef4444"
              title="Solitaire (Klondike)"
              description="The original Windows time-killer. Click-to-move, double-click sends to foundation, undo, draw 1 or draw 3. Win counter saved locally."
              filename="solitaire.html"
              instructions={[
                'Click "Download solitaire.html".',
                "Open the downloaded file in any browser.",
                "Click stock to deal. Click a card, then click destination.",
                "Double-click a card to auto-send it to its foundation.",
              ]}
            />

            {/* Connect Four */}
            <GameCard
              emoji="🔴"
              emojiColor="#fbbf24"
              title="Connect Four (1P / 2P)"
              description="Drop pieces, get 4 in a row. Play vs CPU (Easy / Normal / Hard with proper minimax) or grab a friend for couch 2-player. Persistent score."
              filename="connect4.html"
              instructions={[
                'Click "Download connect4.html".',
                "Open the downloaded file in any browser.",
                "Click a column or press 1–7 to drop your piece.",
                "Pick mode + difficulty in the side panel.",
              ]}
            />

            {/* Breakout */}
            <GameCard
              emoji="🧱"
              emojiColor="#fb923c"
              title="Breakout"
              description="Paddle, ball, bricks. Endless levels with escalating speed and tougher rows. Mouse, touch, or keyboard controls. Best score saved."
              filename="breakout.html"
              instructions={[
                'Click "Download breakout.html".',
                "Open the downloaded file in any browser.",
                "Move mouse, drag on touch, or use ← / → keys.",
                "Space to launch the ball. Don't let it fall!",
              ]}
            />

            {/* Tank Battle */}
            <GameCard
              emoji="💥"
              emojiColor="#22d3ee"
              title="Tank Battle (1P / 2P)"
              description="Top-down arena combat. 1P vs CPU (Easy / Normal / Hard — the AI rotates, predicts, dodges, and respects walls) or 2P couch. Bullets bounce. 3 HP. First to 5."
              filename="tank_battle.html"
              instructions={[
                'Click "Download tank_battle.html".',
                "Open the downloaded file in any browser.",
                "P1: WASD + F shoot. P2: arrows + / shoot.",
                "Pick mode (CPU / 2P) and difficulty in the controls bar.",
              ]}
            />

            {/* Basketball */}
            <GameCard
              emoji="🏀"
              emojiColor="#fb923c"
              title="Basketball (1P / 2P)"
              description="Side-view hoops, ball physics, charged shots, jump-shots, steals, and rebounds. 1P vs CPU (Easy / Normal / Hard) or 2P couch. First to 21 (or highest score after the 2:00 quarter). Smooth on Chromebooks."
              filename="basketball.html"
              instructions={[
                'Click "Download basketball.html".',
                "Open the downloaded file in any browser.",
                "P1: A/D move · W jump · F shoot/steal.",
                "P2: ← / → move · ↑ jump · / shoot/steal.",
                "Hold the shoot key to charge a longer shot. Score in the OPPONENT'S hoop.",
              ]}
            />

            {/* Basket Random */}
            <GameCard
              emoji="🏀"
              emojiColor="#77e9ff"
              title="Basket Random"
              description="One-button basketball chaos with jump-and-swing physics, round-to-round stat randomness, 2P couch play, and a CPU mode for solo matches."
              filename="basket_random.html"
              instructions={[
                'Click "Download basket_random.html".',
                "Open the downloaded file in any browser.",
                "P1 uses W or Space. P2 uses ↑, or switch to CPU mode with the Mode button.",
                "Every press makes your player jump and swing at the ball.",
                "First to 5 scores wins the match.",
              ]}
            />

            {/* Skyline Sprint */}
            <GameCard
              emoji="🏃"
              emojiColor="#7ce7ff"
              title="Skyline Sprint"
              description="A glossy three-lane endless runner with jumps, slides, drones, laser gates, collectible shards, and a neon rooftop skyline."
              filename="endless_runner.html"
              instructions={[
                'Click "Download endless_runner.html".',
                "Open the downloaded file in any browser.",
                "A / D or ← / → to switch lanes.",
                "W / ↑ / Space to jump · S / ↓ to slide.",
                "Dodge drones, clear gates, and collect shards.",
              ]}
            />

            {/* Neon Night Pinball */}
            <GameCard
              emoji="🎯"
              emojiColor="#ff78c8"
              title="Neon Night Pinball"
              description="A single-file neon pinball table with a chargeable plunger, glowing bumpers, orbit spinners, live flippers, and full cabinet-style art."
              filename="pinball.html"
              instructions={[
                'Click "Download pinball.html".',
                "Open the downloaded file in any browser.",
                "Hold Space and release to launch the ball.",
                "A / ← controls the left flipper · D / → controls the right flipper.",
                "Keep the ball out of the drain and chase a higher score.",
              ]}
            />

            {/* Midnight Mole Mayhem */}
            <GameCard
              emoji="🔨"
              emojiColor="#ffd86c"
              title="Midnight Mole Mayhem"
              description="A carnival-style whack-a-mole board with animated characters, golden bonus moles, bomb fake-outs, combo scoring, and a swinging hammer cursor."
              filename="whack_a_mole.html"
              instructions={[
                'Click "Download whack_a_mole.html".',
                "Open the downloaded file in any browser.",
                "Move the mouse or your finger to aim the hammer.",
                "Click or tap moles as they pop up.",
                "Golden moles score bigger · bomb moles punish bad swings.",
              ]}
            />

            {/* Meteor Mesa Dino Dash */}
            <GameCard
              emoji="🦖"
              emojiColor="#98ff88"
              title="Meteor Mesa Dino Dash"
              description="A polished dinosaur runner with layered desert scenery, jump-and-duck obstacle flow, flying pterosaurs, and a speed ramp that keeps climbing."
              filename="dinosaur_dash.html"
              instructions={[
                'Click "Download dinosaur_dash.html".',
                "Open the downloaded file in any browser.",
                "W / ↑ / Space to jump.",
                "Hold S / ↓ to duck under low flyers.",
                "Survive as the mesa speeds up.",
              ]}
            />

            {/* Neon Flux Dash */}
            <GameCard
              emoji="🟪"
              emojiColor="#77e9ff"
              title="Neon Flux Dash"
              description="A geometry-dash-inspired neon auto-runner with spikes, jump pads, floating platforms, saws, gaps, and a handcrafted side-scrolling level."
              filename="geometry_dash.html"
              instructions={[
                'Click "Download geometry_dash.html".',
                "Open the downloaded file in any browser.",
                "Space / W / ↑ or click the playfield to jump.",
                "Hit jump pads, avoid spikes and saws, and reach the end of the level.",
              ]}
            />

            {/* Neon Soccer Duel */}
            <GameCard
              emoji="⚽"
              emojiColor="#7df9ff"
              title="Neon Soccer Duel (2P)"
              description="Stadium-lit couch soccer with animated crowd glow, dash tackles, midfield boost pads, power shots, richer player art, and first-to-5 chaos."
              filename="neon_soccer_duel.html"
              instructions={[
                'Click "Download neon_soccer_duel.html".',
                "Open the downloaded file in any browser.",
                "P1: WASD move · F dash.",
                "P2: arrow keys move · / dash.",
                "Dash into the ball for power shots. Use boost pads to refill dash.",
              ]}
            />

            {/* Wizard Wars */}
            <GameCard
              emoji="🪄"
              emojiColor="#d8b4fe"
              title="Wizard Wars (2P)"
              description="A neon spell duel with cover walls, bouncing magic bolts, shield parries, HP bars, particles, and clutch comeback energy."
              filename="wizard_wars.html"
              instructions={[
                'Click "Download wizard_wars.html".',
                "Open the downloaded file in any browser.",
                "P1: WASD move · F cast · G shield.",
                "P2: arrow keys move · / cast · . shield.",
                "Use walls for cover and shields to reflect spells.",
              ]}
            />

            {/* Jetpack Joust */}
            <GameCard
              emoji="🚀"
              emojiColor="#67e8f9"
              title="Jetpack Joust (2P)"
              description="Two-player aerial arena combat. Boost upward, lance-dash sideways, stomp from above, and knock your friend out of the sky."
              filename="jetpack_joust.html"
              instructions={[
                'Click "Download jetpack_joust.html".',
                "Open the downloaded file in any browser.",
                "P1: A/D move · W boost · F lance.",
                "P2: ← / → move · ↑ boost · / lance.",
                "Hit from above or lance-dash into your opponent. First to 7.",
              ]}
            />

            {/* Sumo Bumpers */}
            <GameCard
              emoji="🛞"
              emojiColor="#fdba74"
              title="Sumo Bumpers (2P)"
              description="A shrinking-ring bumper brawl with chunky knockbacks, boost dashes, powerups, particle bursts, and first-to-5 rounds."
              filename="sumo_bumpers.html"
              instructions={[
                'Click "Download sumo_bumpers.html".',
                "Open the downloaded file in any browser.",
                "P1: WASD move · F boost.",
                "P2: arrow keys move · / boost.",
                "Knock the other player out of the ring before it shrinks.",
              ]}
            />
          </div>

          <div
            style={{
              maxWidth: 720,
              background: "#fff",
              border: "1px solid #e3e7f1",
              borderRadius: 14,
              padding: "16px 20px",
              fontSize: "0.92rem",
              color: "#444",
              lineHeight: 1.55,
              marginBottom: 24,
            }}
          >
            <strong>For advanced students (Mac, Apple Silicon):</strong> the
            project also includes the official{" "}
            <a
              href="https://stockfishchess.org/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", fontWeight: 600 }}
            >
              Stockfish
            </a>{" "}
            chess engine binary in <code>src/stockfish/</code>. It's a
            command-line UCI engine (no built-in board). Pair it with any UCI
            GUI to study positions at world-class strength — completely offline.
          </div>

          <p
            style={{
              fontSize: "0.95rem",
              color: "#666",
              maxWidth: 600,
              textAlign: "center",
            }}
          >
            25 offline games (or grab the Game Hub above for all of them in one file!).
          </p>
        </section>
      </main>
    </>
  );
}

export default OfflineGames;
